/** All URL-derived state for the Route tab. */
export interface RouteUrlState {
  origin: Endpoint | undefined;
  dest: Endpoint | undefined;
  /** HH:MM departure time, or `undefined` for "now". */
  time: string | undefined;
  /** Comma-separated line names to block. */
  blockLines: string;
  /** Comma-separated mode tokens to block. */
  blockModes: string;
}

import type { Endpoint } from '../endpoint';
import { parseFiniteNumber, parseNonNegativeInt } from './_shared';

const HHMM_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const COORD_DECIMALS = 5;

function parsePositionParameter(
  rawParameter: string | null | undefined,
): { latitude: number; longitude: number } | undefined {
  if (!rawParameter) return undefined;
  const parts = rawParameter.split(',');
  if (parts.length !== 2) return undefined;
  const latitude = parseFiniteNumber(parts[0]);
  const longitude = parseFiniteNumber(parts[1]);
  if (latitude == undefined || longitude == undefined) return undefined;
  return { latitude, longitude };
}

function parseEndpoint(parameters: URLSearchParams, prefix: 'from' | 'to'): Endpoint | undefined {
  const stopId = parseNonNegativeInt(parameters.get(`${prefix}_stop`));
  if (stopId != undefined) return { kind: 'stop', stopId };
  const address = parameters.get(`${prefix}_address`);
  if (address) return { kind: 'address', address };
  const position = parsePositionParameter(parameters.get(prefix));
  if (position) return { kind: 'position', ...position };
  return undefined;
}

function encodeEndpoint(
  output: URLSearchParams,
  prefix: 'from' | 'to',
  endpoint: Endpoint | undefined,
): void {
  if (!endpoint) return;
  if (endpoint.kind === 'stop') {
    output.set(`${prefix}_stop`, String(endpoint.stopId));
    return;
  }
  if (endpoint.kind === 'address') {
    output.set(`${prefix}_address`, endpoint.address);
    return;
  }
  output.set(
    prefix,
    `${endpoint.latitude.toFixed(COORD_DECIMALS)},${endpoint.longitude.toFixed(COORD_DECIMALS)}`,
  );
}

/**
 * Reads Route-tab state from URL params. Endpoint mode priority per side is
 * `{prefix}_stop` (stop id) → `{prefix}_address` → `{prefix}` (combined
 * `lat,lng`); first populated wins. Malformed values yield `undefined`. Inverse of
 * {@link encodeRouteUrl}.
 */
export function parseRouteUrl(parameters: URLSearchParams): RouteUrlState {
  const rawTime = parameters.get('time');
  const time = rawTime && HHMM_RE.test(rawTime) ? rawTime : undefined;
  return {
    origin: parseEndpoint(parameters, 'from'),
    dest: parseEndpoint(parameters, 'to'),
    time,
    blockLines: parameters.get('block_lines') ?? '',
    blockModes: parameters.get('block_modes') ?? '',
  };
}

/**
 * Serializes Route-tab state into fresh URL params. Undefined endpoints and empty
 * strings are omitted so the URL stays tidy. Inverse of {@link parseRouteUrl}.
 */
export function encodeRouteUrl(state: RouteUrlState): URLSearchParams {
  const output = new URLSearchParams();
  encodeEndpoint(output, 'from', state.origin);
  encodeEndpoint(output, 'to', state.dest);
  if (state.time) output.set('time', state.time);
  if (state.blockLines) output.set('block_lines', state.blockLines);
  if (state.blockModes) output.set('block_modes', state.blockModes);
  return output;
}
